/**
 * Device detection utilities for mobile optimization
 */

/**
 * Detects if the current device is a mobile device
 * Based on user agent and touch support
 */
export function isMobile(): boolean {
    if (typeof window === 'undefined') return false;
    
    // Check for touch support
    const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    
    // Check user agent for mobile patterns
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
    const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
    const isMobileUA = mobileRegex.test(userAgent);
    
    // Also check screen width as fallback
    const isSmallScreen = window.innerWidth <= 768;
    
    return hasTouch && (isMobileUA || isSmallScreen);
}

/**
 * Detects if the current device is iOS (iPhone, iPad, iPod)
 */
export function isIOS(): boolean {
    if (typeof window === 'undefined') return false;
    
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
    return /iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream;
}

/**
 * Detects if the current browser is Safari (including iOS Safari)
 */
export function isSafari(): boolean {
    if (typeof window === 'undefined') return false;
    
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
    return /^((?!chrome|android).)*safari/i.test(userAgent);
}

/**
 * Gets a safe device pixel ratio for WebGL rendering
 * Mobile devices, especially iOS Safari, can have high DPR (up to 3x)
 * which can cause memory issues and crashes
 * 
 * @returns Safe DPR value capped at 1 for mobile, 2 for desktop
 */
export function getSafeDPR(): number {
    if (typeof window === 'undefined') return 1;
    
    const dpr = window.devicePixelRatio || 1;
    
    // Cap at 1 for mobile devices to prevent crashes
    if (isMobile()) {
        return Math.min(1, dpr);
    }
    
    // Desktop can handle up to 2x
    return Math.min(2, dpr);
}

/**
 * Gets DPR array for react-three-fiber Canvas component
 * @returns [min, max] DPR array
 */
export function getSafeDPRRange(): [number, number] {
    const safeDPR = getSafeDPR();
    return [1, safeDPR];
}

